import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { prepareSticker, useUploadSticker, STICKER_ACCEPT, type PreparedSticker } from '../api';

type ItemState = 'ready' | 'uploading' | 'done' | 'failed';

interface Item {
  prepared: PreparedSticker;
  /** Editable: it becomes the sticker id, and ids are immutable once published. */
  name: string;
  previewUrl: string;
  state: ItemState;
  error?: string;
}

/**
 * Upload images into one pack.
 *
 * <p>Names are editable because the name becomes the sticker id and the id is
 * permanent: later artwork corrections need a new immutable id. Display names
 * and search metadata can be edited separately after uploading.
 *
 * <p>Uploads run one at a time and each is independent. A partial batch is a
 * valid outcome: the ones that landed are live, the ones that failed can be
 * retried without duplicating anything (the backend refuses an existing id).
 */
export function StickerUploadDialog({
  packId,
  packLabel,
  open,
  onClose,
}: {
  packId: string;
  packLabel: string;
  open: boolean;
  onClose: () => void;
}) {
  const [items, setItems] = useState<Item[]>([]);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadSticker();

  // Object URLs are a leak if they outlive the dialog.
  useEffect(() => {
    if (open) return;
    setItems((current) => {
      current.forEach((i) => URL.revokeObjectURL(i.previewUrl));
      return [];
    });
    setBusy(false);
  }, [open]);

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const added: Item[] = [];
    for (const file of Array.from(files)) {
      try {
        const prepared = await prepareSticker(file);
        added.push({
          prepared,
          name: prepared.name,
          previewUrl: URL.createObjectURL(file),
          state: 'ready',
        });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : `${file.name} was rejected`);
      }
    }
    setItems((current) => [...current, ...added]);
  };

  const duplicateNames = new Set(
    items.map((i) => i.name).filter((name, idx, all) => all.indexOf(name) !== idx),
  );
  const pending = items.filter((i) => i.state === 'ready' || i.state === 'failed');
  const canUpload = pending.length > 0 && duplicateNames.size === 0 && !busy;

  const onUpload = async () => {
    setBusy(true);
    let failures = 0;
    for (const [index, item] of items.entries()) {
      if (item.state === 'done') continue;
      setItems((c) => c.map((v, i) => (i === index ? { ...v, state: 'uploading' } : v)));
      try {
        await upload.mutateAsync({
          packId,
          sticker: { ...item.prepared, name: item.name },
        });
        setItems((c) => c.map((v, i) => (i === index ? { ...v, state: 'done' } : v)));
      } catch (err) {
        failures += 1;
        const message = err instanceof Error ? err.message : 'Upload failed';
        setItems((c) =>
          c.map((v, i) => (i === index ? { ...v, state: 'failed', error: message } : v)),
        );
      }
    }
    setBusy(false);
    if (failures === 0) {
      toast.success(`Uploaded ${items.length} sticker${items.length === 1 ? '' : 's'}`);
      onClose();
    } else {
      toast.error(`${failures} of ${items.length} uploads failed — see the list`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload stickers to {packLabel}</DialogTitle>
          <DialogDescription>
            Transparent PNG, up to 2&nbsp;MB each. The server creates a thumbnail for browsing. The
            file name becomes the sticker&apos;s permanent id.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept={STICKER_ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => {
              void addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="size-4" />
            Add images
          </Button>

          {items.length > 0 && (
            <div className="max-h-80 space-y-2 overflow-y-auto rounded-md border p-2">
              {items.map((item, index) => (
                <div key={item.previewUrl} className="flex items-center gap-3">
                  <img
                    src={item.previewUrl}
                    alt=""
                    className="size-12 shrink-0 rounded border object-contain"
                  />
                  <div className="min-w-0 flex-1 space-y-1">
                    <Input
                      value={item.name}
                      disabled={busy || item.state === 'done'}
                      onChange={(e) =>
                        setItems((c) =>
                          c.map((v, i) => (i === index ? { ...v, name: e.target.value } : v)),
                        )
                      }
                      className={cn(
                        'h-8 font-mono text-xs',
                        duplicateNames.has(item.name) && 'border-destructive',
                      )}
                    />
                    <p className="text-xs text-muted-foreground">
                      {item.prepared.width}×{item.prepared.height} ·{' '}
                      {(item.prepared.file.size / 1024).toFixed(0)} KB · id{' '}
                      <span className="font-mono">
                        {packId}__{item.name}
                      </span>
                    </p>
                    {item.error && <p className="text-xs text-destructive">{item.error}</p>}
                  </div>
                  <div className="w-8 shrink-0 text-center">
                    {item.state === 'uploading' && <Loader2 className="size-4 animate-spin" />}
                    {item.state === 'done' && <Check className="size-4 text-emerald-500" />}
                    {(item.state === 'ready' || item.state === 'failed') && !busy && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setItems((c) => {
                            const target = c[index];
                            if (target) URL.revokeObjectURL(target.previewUrl);
                            return c.filter((_, i) => i !== index);
                          })
                        }
                      >
                        <X className="size-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {duplicateNames.size > 0 && (
            <p className="text-xs text-destructive">
              Two files share a name — ids must be unique within a pack.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
            Close
          </Button>
          <Button type="button" disabled={!canUpload} onClick={() => void onUpload()}>
            {busy ? 'Uploading…' : `Upload ${pending.length || ''}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
